import {
  addSessionSigners,
  getUserEmbeddedEthereumWallet,
  getWallet,
  isUnifiedWallet,
} from '@privy-io/js-sdk-core';
import { privyConfig } from '@/config/privy';
import { ensurePrivyEmbeddedReady, getPrivyClient, getPrivySessionUser } from '@/utils/privy';

function normalizeSignerId(value) {
  return String(value ?? '').trim();
}

function collectWalletSigners(wallet) {
  if (!wallet) return [];
  const raw = wallet.additional_signers ?? wallet.additionalSigners ?? wallet.signers ?? [];
  return Array.isArray(raw) ? raw : [];
}

function walletHasSigner(wallet, signerKeyId) {
  if (!wallet || !signerKeyId) return false;
  const target = normalizeSignerId(signerKeyId);
  return collectWalletSigners(wallet).some((item) => {
    const id = normalizeSignerId(
      item?.signer_id ?? item?.signerId ?? item?.id ?? item?.key_quorum_id
    );
    return id && id === target;
  });
}

function isDuplicateSignerError(err) {
  const msg = String(
    err?.message ?? err?.error ?? err?.response?.data?.error ?? err ?? ''
  ).toLowerCase();
  return msg.includes('duplicate signer');
}

/**
 * 用户确认授权：调用 Privy addSessionSigners，将 App Signer 挂到嵌入式钱包。
 * TEE/unified 钱包传 signer_id；on-device 钱包传空数组走 delegate。
 * 已授权 / Duplicate signer 均视为成功（幂等）。
 */
export async function grantPrivyAppSigner(user) {
  const signerKeyId = privyConfig.signerKeyId;
  if (!signerKeyId) {
    throw new Error('VUE_APP_PRIVY_SIGNER_KEY_ID is not configured');
  }

  await ensurePrivyEmbeddedReady();
  const client = await getPrivyClient();
  if (!client) {
    throw new Error('Privy is not configured');
  }

  // 登录瞬间的 user 常缺 additional_signers，先拉会话最新态
  let currentUser = null;
  try {
    const { user: fresh } = await client.user.get();
    currentUser = fresh?.id ? fresh : null;
  } catch {
    currentUser = null;
  }
  if (!currentUser?.id) {
    currentUser = user?.id ? user : await getPrivySessionUser();
  }
  if (!currentUser?.id) {
    throw new Error('Privy user is missing');
  }

  let wallet = getUserEmbeddedEthereumWallet(currentUser);
  if (!wallet?.address) {
    throw new Error('Privy embedded wallet is missing');
  }

  if (wallet.delegated === true || walletHasSigner(wallet, signerKeyId)) {
    return { user: currentUser, skipped: true };
  }

  // unified：以 Wallet API 为准，避免 user 对象未同步导致重复 add
  if (isUnifiedWallet(wallet) && wallet.id) {
    try {
      const remote = await getWallet(client, { wallet_id: wallet.id });
      if (walletHasSigner(remote, signerKeyId)) {
        const refreshed = await getPrivySessionUser();
        return { user: refreshed ?? currentUser, skipped: true };
      }
    } catch (err) {
      console.warn('[privy] getWallet before grant failed', err);
    }
  }

  const signers = isUnifiedWallet(wallet)
    ? [{ signer_id: signerKeyId, override_policy_ids: [] }]
    : [];

  try {
    const { user: updatedUser } = await addSessionSigners({
      client,
      wallet,
      signers,
    });
    return { user: updatedUser ?? currentUser, skipped: false };
  } catch (err) {
    // 上次已挂上 signer，重试登录会撞 Duplicate —— 视为已授权
    if (isDuplicateSignerError(err)) {
      console.warn('[privy] addSessionSigners duplicate, treat as granted', err);
      const refreshed = await getPrivySessionUser();
      return { user: refreshed ?? currentUser, skipped: true };
    }
    throw err;
  }
}
